import { Injectable } from '@angular/core';
import { GroupComparator, HeaderWithIconTemplate, StarredCellRendererParams } from '@y42/ag-grid';
import { ListViewColDef } from '@y42/core/list-view';
import { DateTimeService } from '@y42/date-time';
import { agGridTableIcon, agGridTagIcon } from '@y42/feature/icons';
import { EntityTypeCellRendererParams, SubEntryRendererParams } from '@y42/feature/shared/list-view';
import { jobReadyIcon } from '@y42/job';
import {
  JobStatusCellRendererComponent,
  JobStatusHeaderRendererComponent,
  JobStatusValueFormatter,
} from '@y42/job/ag-grid';
import { formatValueConversion } from '@y42/util/format-value-conversion';
import { isNotNullish } from '@y42/util/nullish';
import { DatasourceListViewData } from '../../state/datasource/datasource.model';

@Injectable({ providedIn: 'root' })
export class DatasourceListViewColumnDefsService {
  constructor(private readonly dateTimeService: DateTimeService) {}

  columnDefs(cfg: {
    locale?: string;
    disableStarring?: boolean;
    disableTagging?: boolean;
    disableSelection?: boolean;
    hideDetails?: boolean;
    previewButton?: boolean;
    tablePreview?: SubEntryRendererParams['tablePreview'];
    generateQuery?: SubEntryRendererParams['generateQuery'];
    toggleStarred?: (id: DatasourceListViewData['$path']) => void;
    generateQueryButton?: boolean;
  }): Partial<Record<keyof DatasourceListViewData, ListViewColDef>> {
    return {
      id: {
        headerName: 'Name',
        colId: 'id',
        field: 'id',
        enableRowGroup: true,
        rowGroup: true,
        hide: true,
        order: 10,
        valueGetter: (params) => params.data?.name,
        aggFunc: 'pathAggFunc',
      },
      name: {
        headerName: 'Name',
        colId: 'name',
        field: 'name',
        hide: true,
        order: 20,
      },
      starred: {
        headerName: 'Star',
        field: 'starred',
        filter: 'agSetColumnFilter',
        aggFunc: 'first',
        cellRenderer: 'starred',
        minWidth: 70,
        maxWidth: 110,
        sort: 'desc',
        sortIndex: 0,
        cellRendererParams: {
          showGroupOnly: true,
          allowEdit: !cfg.disableStarring,
          setStarred: (node) => cfg.toggleStarred?.(node.aggData.id),
        } as StarredCellRendererParams,
        filterValueGetter: (params) => `${params.data.starred}`,
        order: 30,
      },
      typeModel: {
        headerName: 'Type',
        colId: 'type_display_name',
        field: 'type_display_name',
        aggFunc: 'first',
        cellRenderer: 'entryType',
        cellRendererParams: {
          hideChildIcon: true,
        } as EntityTypeCellRendererParams,
        filter: 'agSetColumnFilter',
        minWidth: 70,
        maxWidth: 80,
        order: 35,
        valueGetter: (params) => {
          const data: DatasourceListViewData | undefined = params.data;
          return data?.typeModel ? data.typeModel.displayName : null;
        },
        filterValueGetter: (params) => {
          const data: DatasourceListViewData | undefined = params.data;
          return data?.typeModel ? data.typeModel.displayName : null;
        },
      },
      tags: {
        headerName: 'Tags',
        colId: 'tags',
        field: 'tags',
        minWidth: 140,
        headerComponentParams: {
          template: HeaderWithIconTemplate(agGridTagIcon),
        },
        aggFunc: 'tag',
        cellRenderer: 'tags',
        filter: 'agSetColumnFilter',
        filterValueGetter: (params) =>
          params.data.tags?.length
            ? (params.data as DatasourceListViewData).tags.map((tag: string) => tag)
            : 'Untagged',
        order: 40,
      },
      child_name: {
        headerName: 'Tables',
        colId: 'child_name',
        field: 'child_name',
        checkboxSelection: !cfg.disableSelection,
        filter: 'agTextColumnFilter',
        minWidth: 200,
        suppressSizeToFit: true,
        cellRenderer: 'subEntry',
        cellRendererParams: {
          previewButton: cfg.previewButton === undefined ? !cfg.disableSelection : cfg.previewButton,
          generateQueryButton: !!cfg.generateQueryButton,
          hideDetails: cfg.hideDetails,
          generateQuery: cfg.generateQuery,
          tablePreview: cfg.tablePreview,
        } as SubEntryRendererParams,
        headerComponentParams: {
          template: HeaderWithIconTemplate(agGridTableIcon),
        },
        order: 11,
      },
      description: {
        headerName: 'Description',
        colId: 'description',
        aggFunc: 'description',
        field: 'description',
        valueGetter: (params) => {
          const data: DatasourceListViewData = params.data;
          const level = params.node?.level;
          return {
            child_description: data?.child_description,
            description: data?.description,
            toString: () => (level ? data?.child_description : data?.description) || '',
          };
        },
        minWidth: 160,
        menuTabs: ['filterMenuTab', 'generalMenuTab'],
        filter: 'agTextColumnFilter',
        order: 503,
      },
      // asset_status: {
      //   headerName: 'Status',
      //   colId: 'asset_status',
      //   field: 'asset_status',
      //   aggFunc: 'assetStatus',
      //   filter: 'agTextColumnFilter',
      //   cellRenderer: 'assetStatus',
      //   order: 505,
      //   width: 110,
      //   minWidth: 60,
      //   headerComponentParams: {
      //     enableSorting: true,
      //   },
      // },
      rows: {
        headerName: 'Rows',
        field: 'rows',
        aggFunc: 'sum',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params) =>
          isNotNullish(params.value) ? formatValueConversion(params.value, cfg.locale ?? 'en', 'Automatic', 1) : '',
        minWidth: 100,
        maxWidth: 140,
        order: 500,
      },
      child_status: {
        headerName: 'Final Status',
        field: 'child_status',
        aggFunc: 'distribution',
        filter: 'agSetColumnFilter',
        comparator: GroupComparator,
        cellRenderer: JobStatusCellRendererComponent,
        minWidth: 200,
        maxWidth: 200,
        getQuickFilterText: JobStatusValueFormatter,
        filterParams: {
          valueFormatter: JobStatusValueFormatter,
        },
        headerClass: '',
        headerComponent: JobStatusHeaderRendererComponent,
        headerComponentParams: {
          enableSorting: true,
          enableMenu: true,
        },
        order: 501,
      },
      last_import_date: {
        headerName: 'Last Valid Job',
        field: 'last_import_date',
        aggFunc: 'max',
        filter: 'dateFilter',
        minWidth: 120,
        headerComponentParams: {
          template: HeaderWithIconTemplate(jobReadyIcon),
        },
        valueGetter: (params) => {
          const value = params.data['last_import_date'];
          return isNaN(value) ? 0 : value;
        },
        valueFormatter: (params) => (params.value ? this.dateTimeService.formatDateRelativeToNow(params.value) : '-'),
        order: 505,
      },
    };
  }
}
